import { useState } from "react";
import { Loader2, LogIn, ShieldCheck } from "lucide-react";
import { LeafMark } from "@/components/LeafMark";
import { api } from "../api";

export const AdminLogin = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError("Please enter your email and password.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            onLogin(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Login failed. Please try again.");
            setLoading(false);
        }
    };

    const quickFill = (value) => {
        setEmail(value);
        setError("");
    };

    return (
        <div className="min-h-screen bg-[#0B1513] flex items-center justify-center px-5 py-16" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <div className="rounded-2xl border border-[#1D322E] bg-[#12201D]/90 backdrop-blur-md shadow-lg shadow-black/40 p-8 sm:p-10">
                    <div className="flex flex-col items-center text-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#063C35] border border-[#0B5148]">
                            <LeafMark className="w-8 h-8 text-[#F7F5EF]" />
                        </span>
                        <h1 className="mt-5 font-serif text-3xl text-[#F7F5EF]">Staff Portal</h1>
                        <p className="mt-2 text-sm text-[#A3B8B0]">
                            Sign in to the UniversHeal operations console.
                        </p>
                    </div>

                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
                        <div>
                            <label htmlFor="admin-email" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                Email
                            </label>
                            <input
                                id="admin-email"
                                data-testid="admin-login-email-input"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@healease.health"
                                className="mt-2 w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                            />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                Password
                            </label>
                            <input
                                id="admin-password"
                                data-testid="admin-login-password-input"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••"
                                className="mt-2 w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                            />
                        </div>

                        {error && (
                            <p
                                role="alert"
                                data-testid="admin-login-error"
                                className="rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-300"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            data-testid="admin-login-submit-button"
                            disabled={loading}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#10B981] px-4 py-3 text-sm font-bold text-[#0B1513] transition-all duration-200 hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    Signing in <Loader2 className="w-4 h-4 animate-spin" />
                                </>
                            ) : (
                                <>
                                    Sign in <LogIn className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 border-t border-[#1D322E] pt-5">
                        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#64716D]">
                            <ShieldCheck className="w-3.5 h-3.5" /> Demo quick-fill
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button
                                type="button"
                                data-testid="admin-quick-fill-admin"
                                onClick={() => quickFill("admin@healease.health")}
                                className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors"
                            >
                                Fill admin email
                            </button>
                            <button
                                type="button"
                                data-testid="admin-quick-fill-coordinator"
                                onClick={() => quickFill("coordinator@healease.health")}
                                className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors"
                            >
                                Fill coordinator email
                            </button>
                        </div>
                    </div>
                </div>
                <p className="mt-6 text-center text-xs text-[#64716D]">
                    UniversHeal Operations Console — authorised staff only.
                </p>
            </div>
        </div>
    );
};
